import {
  buildActiveMitigationsTable,
  buildBandCountsTable,
  buildCategoryNextActionText,
  buildFilteredUniqueSupplierCountTable,
  buildGroupedSuppliersTable,
  buildHighestInCategoryText,
  buildIntelligenceSuppliersTable,
  buildMitigationTrackerTable,
  buildMitigationsForSupplierTable,
  buildRecentAlertsTable,
  buildRegionCountsTable,
  buildSpecialReportsTable,
  buildSupplierBrandsTableFromRows,
  buildUniqueSupplierTotalTable,
  type DbTableResult,
} from './dbTableAnswers'
import {
  classifyEarlySignalsIntent,
  type RiskAnalystClassification,
} from './classifyEarlySignalsIntent'
import {
  emptyMatchSentence,
  extractSupplierListFilters,
  newlyHighSuppliersSentence,
} from './earlySignalsDbIntent'
import {
  extractGroupByDimensions,
  isRefineLastTableAsk,
  mergeSupplierListFilters,
  type RiskAnalystListSession,
} from './riskAnalystSession'
import {
  fetchSupplierRowsByName,
  fetchSupplierRowsByParentName,
  groupSupplierRowsByName,
  resolveUniqueSupplierNameKey,
  type SupplierNameMatch,
} from './resolveSupplierByName'
import { supplierDisplayName } from './supplierRollup'
import { fetchRaSupplierRoster } from '../../services/supplierRosterCache'
import { hideSimulatedSuppliers, setSimulatedSupplierCompany } from './simulatedSuppliers'
import {
  buildAmbiguousSuppliersMessage,
  buildAskForBrandsSupplierMessage,
  buildAskForSupplierMessage,
  buildNoSupplierMessage,
  buildSupplierScorecardTable,
  cleanSupplierNameForLookup,
  extractRiskDimensionKey,
  formatHighestRiskAreasSummary,
  wantsSupplierRatingsTable,
  DIMENSION_KEYS,
} from './supplierRiskRatingsTable'
import {
  resolveSupplierDimensionCommentary,
  resolveSupplierProfile,
  type RiskRatingsDbAnswer,
} from './tryAnswerRiskRatingsFromDb'
import {
  answerSpendCategory,
  answerSpendPortfolio,
  answerSpendSupplier,
} from './spendAnalystAnswers'

export type { ListTableIntent } from './earlySignalsDbIntent'
export { matchListTableIntent } from './earlySignalsDbIntent'
export { classifyEarlySignalsIntent } from './classifyEarlySignalsIntent'

export type EarlySignalsDbAnswer =
  | RiskRatingsDbAnswer
  | { kind: 'table'; content: string; table: DbTableResult }
  | {
      kind: 'narrative'
      narrative: 'next_best_actions' | 'questions_to_ask'
      supplierName: string | null
    }

export type TryAnswerEarlySignalsOptions = {
  /** Last list/count/group table context for follow-up refine. */
  session?: RiskAnalystListSession | null
  /**
   * Portfolio Direct/Indirect default when the utterance does not name one
   * (aligned with Risk Overview / Intelligence View sessionStorage).
   */
  defaultProcurementType?: 'direct' | 'indirect' | 'all'
  /** Active company, including View as. Spend tools never blend companies. */
  companyId?: string | null
  /** Admin Spend nav: link supplier names to the Spend page. */
  linkSpend?: boolean
  /**
   * Dimension key from a classifier slot, used only when the question text
   * does not already name a dimension.
   */
  dimensionKey?: string | null
  /** Category Analyst: answer only for these supplier ids. Risk Analyst leaves this unset. */
  allowedSupplierIds?: ReadonlySet<string>
  /** Category on the open brief, used when a spend question is not already named. */
  categoryScope?: string
}

function withDefaultProcurement(
  filters: ReturnType<typeof extractSupplierListFilters>,
  defaultProcurementType?: 'direct' | 'indirect' | 'all'
) {
  if (filters.procurementType || !defaultProcurementType) return filters
  return { ...filters, procurementType: defaultProcurementType }
}

function withAllowed(
  filters: ReturnType<typeof extractSupplierListFilters>,
  allowed?: ReadonlySet<string>
) {
  if (!allowed) return filters
  return { ...filters, supplierIds: allowed }
}

function notInBrief(name: string): string {
  return `${name} is not in this category for this company.`
}

/** Demo names are "Supplier 236". The shared name parser treats the word supplier as a stop word. */
function supplierNumberAlias(text: string): string | null {
  const match = text.match(/\bsupplier\s+\d+\b/i)
  return match ? match[0] : null
}

async function namesForIds(ids: ReadonlySet<string>): Promise<Set<string>> {
  const rows = hideSimulatedSuppliers(await fetchRaSupplierRoster())
  const names = new Set<string>()
  for (const row of rows) {
    if (!row.id || !ids.has(row.id)) continue
    const name = (row.name || '').trim().toLowerCase()
    const parent = (row.parent_name || '').trim().toLowerCase()
    if (name) names.add(name)
    if (parent) names.add(parent)
  }
  return names
}

function restrictSpendRows(
  answer: EarlySignalsDbAnswer,
  names: Set<string>
): EarlySignalsDbAnswer {
  if (answer.kind !== 'table') return answer
  const rows: string[][] = []
  const hrefs: (string | null)[] = []
  answer.table.rows.forEach((row, index) => {
    const name = String(row[0] || '').trim().toLowerCase()
    if (!names.has(name)) return
    rows.push(row)
    hrefs.push(answer.table.rowHrefs?.[index] ?? null)
  })
  if (rows.length === 0) {
    return { kind: 'text', content: 'No spend on this company’s file for suppliers in this category.' }
  }
  return {
    kind: 'table',
    content: `${rows.length} supplier${rows.length === 1 ? '' : 's'} in this category are on the spend file.`,
    table: {
      ...answer.table,
      rows,
      rowHrefs: hrefs.some(Boolean) ? hrefs : undefined,
    },
  }
}

function tableAnswer(table: DbTableResult): EarlySignalsDbAnswer {
  if (table.rows.length === 0) {
    return { kind: 'text', content: emptyMatchSentence(table.title) }
  }
  const n = table.rows.length
  return {
    kind: 'table',
    content: `${table.title}: ${n} row${n === 1 ? '' : 's'} from platform data. Next question: “Which of these should I open?”`,
    table,
  }
}

async function resolveNamedRows(
  nameQuery: string,
  allowed?: ReadonlySet<string>
): Promise<
  | { error: string; choices?: string[] }
  | { chosen: SupplierNameMatch[]; displayName: string }
> {
  let query = nameQuery
  let rows = await fetchSupplierRowsByName(query)
  if (rows.length === 0) {
    const cleaned = cleanSupplierNameForLookup(nameQuery)
    if (cleaned && cleaned.toLowerCase() !== nameQuery.trim().toLowerCase()) {
      query = cleaned
      rows = await fetchSupplierRowsByName(query)
    }
  }
  if (rows.length === 0) {
    const display = cleanSupplierNameForLookup(nameQuery) || nameQuery
    return { error: buildNoSupplierMessage(display) }
  }
  const scoped = allowed ? rows.filter((row) => row.id && allowed.has(row.id)) : rows
  if (allowed && scoped.length === 0) {
    return { error: notInBrief(rows[0]?.name?.trim() || nameQuery) }
  }
  const byName = groupSupplierRowsByName(scoped)
  const key = resolveUniqueSupplierNameKey(query, [...byName.keys()])
  if (!key) {
    const names = [...byName.keys()].map((k) => byName.get(k)?.[0]?.name || k)
    return { error: buildAmbiguousSuppliersMessage(names), choices: names }
  }
  let chosen = byName.get(key) ?? []
  const seed = chosen[0]
  const parent = (seed?.parent_name || '').trim()
  if (seed?.procurement_type === 'indirect' && parent) {
    let group = await fetchSupplierRowsByParentName(parent)
    if (allowed) group = group.filter((row) => row.id && allowed.has(row.id))
    if (group.length > 0) chosen = group
  }
  const displayName = (seed && supplierDisplayName(seed)) || query
  return { chosen, displayName }
}

/** Unique legal name, a clickable list, or no match. Used before next-best prose. */
export async function resolveNarrativeSupplier(
  nameQuery: string
): Promise<
  | { kind: 'unique'; name: string }
  | { kind: 'ambiguous'; names: string[]; content: string }
  | { kind: 'none'; content: string }
> {
  const resolved = await resolveNamedRows(nameQuery)
  if ('error' in resolved) {
    if (resolved.choices && resolved.choices.length > 0) {
      return { kind: 'ambiguous', names: resolved.choices, content: resolved.error }
    }
    return { kind: 'none', content: resolved.error }
  }
  return { kind: 'unique', name: resolved.displayName }
}

async function answerListSession(
  session: RiskAnalystListSession,
  filters = session.filters
): Promise<EarlySignalsDbAnswer> {
  if (session.tool === 'count_filtered_suppliers') {
    return tableAnswer(await buildFilteredUniqueSupplierCountTable(filters))
  }
  if (session.tool === 'group_suppliers_by') {
    return tableAnswer(
      await buildGroupedSuppliersTable(filters, session.groupBy ?? extractGroupByDimensions('by region'))
    )
  }
  const table = await buildIntelligenceSuppliersTable(filters)
  return tableAnswer(table)
}

/**
 * Deterministic Early Signals answers from Supabase (tools + narrative tags).
 * Returns null only for true LLM/RAG fallthrough (caller may try JSON classify next).
 */
export async function tryAnswerEarlySignalsFromDb(
  userText: string,
  options?: TryAnswerEarlySignalsOptions
): Promise<EarlySignalsDbAnswer | null> {
  setSimulatedSupplierCompany(options?.companyId)
  const session = options?.session ?? null
  const defaultProcurementType = options?.defaultProcurementType

  // Follow-up refine: merge filter deltas onto last list/count/group table
  if (session && isRefineLastTableAsk(userText)) {
    const delta = withDefaultProcurement(extractSupplierListFilters(userText), defaultProcurementType)
    const filters = withAllowed(
      mergeSupplierListFilters(session.filters, delta),
      options?.allowedSupplierIds
    )
    return answerListSession({ ...session, filters }, filters)
  }
  if (!session && isRefineLastTableAsk(userText)) {
    return {
      kind: 'text',
      content:
        'I can refine a table once you have one open. Try “Show high-risk suppliers in a table”, then follow up with “now EMEA” or “only single-source”.',
    }
  }

  const routed = classifyEarlySignalsIntent(userText)
  return dispatchEarlySignalsAnswer(routed, userText, options)
}

/**
 * Run one already-chosen tool. Keyword routing and the miss-path classifier
 * both land here. List filters still come from the original question text.
 */
export async function dispatchEarlySignalsAnswer(
  routed: RiskAnalystClassification,
  userText: string,
  options?: TryAnswerEarlySignalsOptions
): Promise<EarlySignalsDbAnswer | null> {
  setSimulatedSupplierCompany(options?.companyId)
  const defaultProcurementType = options?.defaultProcurementType
  const companyId = options?.companyId ?? null
  const linkSpend = options?.linkSpend === true
  const allowed = options?.allowedSupplierIds
  const named = (explicit: string | null | undefined) =>
    explicit || (allowed ? supplierNumberAlias(userText) : null)
  const supplierName = named(routed.kind === 'fallthrough' ? null : routed.supplierName)
  const listFilters = (text: string) =>
    withAllowed(
      withDefaultProcurement(extractSupplierListFilters(text), defaultProcurementType),
      allowed
    )

  if (routed.kind === 'fallthrough') return null

  if (routed.kind === 'narrative') {
    return {
      kind: 'narrative',
      narrative: routed.tool,
      supplierName: supplierName,
    }
  }

  switch (routed.tool) {
    case 'supplier_brands': {
      if (!supplierName) {
        return { kind: 'text', content: buildAskForBrandsSupplierMessage() }
      }
      const resolved = await resolveNamedRows(supplierName, allowed)
      if ('error' in resolved) {
        return { kind: 'text', content: resolved.error, choices: resolved.choices }
      }
      const table = buildSupplierBrandsTableFromRows(resolved.displayName, resolved.chosen)
      if (!table) {
        return {
          kind: 'text',
          content: `No brands are recorded on platform data for “${resolved.displayName}”.`,
        }
      }
      return tableAnswer(table)
    }
    case 'supplier_scorecard': {
      if (!supplierName) {
        return { kind: 'text', content: buildAskForSupplierMessage() }
      }
      if (allowed) {
        const resolved = await resolveNamedRows(supplierName, allowed)
        if ('error' in resolved) {
          return { kind: 'text', content: resolved.error, choices: resolved.choices }
        }
      }
      if (wantsSupplierRatingsTable(userText)) {
        const profile = await resolveSupplierProfile(supplierName)
        if (profile.kind === 'supplier_profile') {
          return tableAnswer(buildSupplierScorecardTable(profile.supplier))
        }
        return profile
      }
      return resolveSupplierProfile(supplierName)
    }
    case 'supplier_dimension_commentary': {
      if (!supplierName) {
        return {
          kind: 'text',
          content:
            'Which supplier’s risk commentary do you want? For example: “Strategic Risk commentary for Gissler & Pass” or “All commentaries for MM Packaging”.',
        }
      }
      let commentaryText = userText
      if (!extractRiskDimensionKey(userText) && options?.dimensionKey) {
        const label = DIMENSION_KEYS.find((d) => d.key === options.dimensionKey)?.label
        if (label) commentaryText = `${userText} ${label} risk`
      }
      if (allowed) {
        const resolved = await resolveNamedRows(supplierName, allowed)
        if ('error' in resolved) {
          return { kind: 'text', content: resolved.error, choices: resolved.choices }
        }
      }
      return resolveSupplierDimensionCommentary(supplierName, commentaryText)
    }
    case 'list_active_mitigations':
      return tableAnswer(await buildActiveMitigationsTable())
    case 'list_alerts': {
      const table = await buildRecentAlertsTable()
      const note = newlyHighSuppliersSentence()
      if (table.rows.length === 0) {
        return {
          kind: 'text',
          content: `No alerts were published in the past 7 days. ${note} Next question: “Tell me about a supplier by name.”`,
        }
      }
      const n = table.rows.length
      return {
        kind: 'table',
        content: `${table.title}: ${n} row${n === 1 ? '' : 's'} from platform data. ${note} Next question: “Which of these suppliers should I open?”`,
        table,
      }
    }
    case 'supplier_highest_areas': {
      if (!supplierName) {
        return {
          kind: 'text',
          content:
            'Which supplier’s highest-risk areas should I summarize? For example: “What is the highest risk in MCC”.',
        }
      }
      if (allowed) {
        const resolved = await resolveNamedRows(supplierName, allowed)
        if ('error' in resolved) {
          return { kind: 'text', content: resolved.error, choices: resolved.choices }
        }
      }
      const profile = await resolveSupplierProfile(supplierName)
      if (profile.kind !== 'supplier_profile') return profile
      return {
        kind: 'text',
        content: formatHighestRiskAreasSummary(profile.supplier),
        resolvedSupplier: (profile.supplier.name || '').trim() || null,
      }
    }
    case 'highest_in_category': {
      const categoryName = routed.categoryName
      if (!categoryName) {
        return {
          kind: 'text',
          content: 'Which category? For example: “Which supplier is highest risk in chemicals”.',
        }
      }
      return {
        kind: 'text',
        content: await buildHighestInCategoryText(categoryName, defaultProcurementType, allowed),
        resolvedCategory: categoryName,
      }
    }
    case 'category_next_action': {
      const categoryName = routed.categoryName
      if (!categoryName) {
        return {
          kind: 'text',
          content: 'Which portfolio? For example: “I am the chemicals manager”.',
        }
      }
      return {
        kind: 'text',
        content: await buildCategoryNextActionText(categoryName, defaultProcurementType, allowed),
        resolvedCategory: categoryName,
      }
    }
    case 'list_suppliers': {
      const filters = listFilters(userText)
      return answerListSession({ tool: 'list_suppliers', filters }, filters)
    }
    case 'list_tracker':
      return tableAnswer(await buildMitigationTrackerTable())
    case 'list_reports': {
      if (!supplierName) {
        return tableAnswer(
          await buildSpecialReportsTable(allowed ? { supplierIds: new Set(allowed) } : undefined)
        )
      }
      const resolved = await resolveNamedRows(supplierName, allowed)
      if ('error' in resolved) {
        return { kind: 'text', content: resolved.error, choices: resolved.choices }
      }
      const ids = new Set(resolved.chosen.map((r) => r.id).filter(Boolean))
      return tableAnswer(
        await buildSpecialReportsTable({
          supplierIds: ids,
          supplierLabel: resolved.displayName,
        })
      )
    }
    case 'supplier_summary': {
      if (!supplierName) {
        return {
          kind: 'text',
          content:
            'Which supplier should I summarize? For example: “Tell me about CHEP” or “Supplier summary for Gissler & Pass”.',
        }
      }
      if (allowed) {
        const resolved = await resolveNamedRows(supplierName, allowed)
        if ('error' in resolved) {
          return { kind: 'text', content: resolved.error, choices: resolved.choices }
        }
      }
      return resolveSupplierProfile(supplierName)
    }
    case 'count_by_band':
      return tableAnswer(await buildBandCountsTable(allowed ? listFilters(userText) : {}))
    case 'count_by_region':
      return tableAnswer(await buildRegionCountsTable(allowed ? listFilters(userText) : {}))
    case 'count_total_suppliers':
      return tableAnswer(await buildUniqueSupplierTotalTable(allowed ? listFilters(userText) : {}))
    case 'count_filtered_suppliers': {
      const filters = listFilters(userText)
      return answerListSession({ tool: 'count_filtered_suppliers', filters }, filters)
    }
    case 'group_suppliers_by': {
      const filters = listFilters(userText)
      const groupBy = extractGroupByDimensions(userText)
      return answerListSession({ tool: 'group_suppliers_by', filters, groupBy }, filters)
    }
    case 'mitigations_for_supplier': {
      if (!supplierName) {
        return {
          kind: 'text',
          content:
            'Which supplier’s mitigations do you want? For example: “Open mitigations for CHEP”.',
        }
      }
      const resolved = await resolveNamedRows(supplierName, allowed)
      if ('error' in resolved) {
        return { kind: 'text', content: resolved.error, choices: resolved.choices }
      }
      const ids = new Set(resolved.chosen.map((r) => r.id).filter(Boolean))
      return tableAnswer(await buildMitigationsForSupplierTable(resolved.displayName, ids))
    }
    case 'spend_portfolio':
      if (allowed && options?.categoryScope) {
        return restrictSpendRows(
          await answerSpendCategory(companyId, options.categoryScope, false),
          await namesForIds(allowed)
        )
      }
      return answerSpendPortfolio(companyId, linkSpend)
    case 'spend_supplier': {
      if (!supplierName) {
        return {
          kind: 'text',
          content: 'Which supplier’s spend should I use? For example: “Spend with CHEP”.',
        }
      }
      if (allowed) {
        const resolved = await resolveNamedRows(supplierName, allowed)
        if ('error' in resolved) {
          return { kind: 'text', content: resolved.error, choices: resolved.choices }
        }
      }
      return answerSpendSupplier(companyId, supplierName, linkSpend)
    }
    case 'spend_category': {
      const categoryName = routed.categoryName || options?.categoryScope
      if (!categoryName) {
        return {
          kind: 'text',
          content: 'Which category? For example: “Spend in category Packaging”.',
        }
      }
      const spend = await answerSpendCategory(companyId, categoryName, linkSpend && !allowed)
      if (!allowed) return spend
      return restrictSpendRows(spend, await namesForIds(allowed))
    }
    default:
      return null
  }
}

/** Build session snapshot after a successful list/count/group answer. */
export function listSessionFromUtterance(
  userText: string,
  tool: RiskAnalystListSession['tool'],
  defaultProcurementType?: 'direct' | 'indirect' | 'all'
): RiskAnalystListSession {
  const filters = withDefaultProcurement(
    extractSupplierListFilters(userText),
    defaultProcurementType
  )
  if (tool === 'group_suppliers_by') {
    return { tool, filters, groupBy: extractGroupByDimensions(userText) }
  }
  return { tool, filters }
}
